import { useState } from 'react';

const URL_REGEX = /https?:\/\/[^\s<>"{}|\\^`[\]]+/g;

function linkAnchor(href: string, display: string, key: number): React.ReactNode {
  return (
    <a
      key={key}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-600 hover:underline break-all"
      onClick={(e) => e.stopPropagation()}
    >
      {display}
    </a>
  );
}

/** Linkify the full text — every URL becomes a clickable anchor. */
export function linkifyText(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  URL_REGEX.lastIndex = 0;
  while ((match = URL_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(linkAnchor(match[0], match[0], match.index));
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
}

/**
 * Linkify a truncated preview of fullText.
 *
 * Matches URLs against the FULL text so the href is always the complete URL,
 * even when the visible display text is cut short by the truncation limit.
 * This prevents broken links when a URL straddles the truncation boundary.
 */
function linkifyTruncated(fullText: string, truncateAt: number): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  URL_REGEX.lastIndex = 0;
  while ((match = URL_REGEX.exec(fullText)) !== null) {
    const urlStart = match.index;
    const urlEnd = match.index + match[0].length;

    // URL starts after the visible window — nothing more to show
    if (urlStart >= truncateAt) break;

    // Plain text segment before this URL
    if (urlStart > lastIndex) {
      parts.push(fullText.slice(lastIndex, urlStart));
    }

    // Anchor: href = full URL, display = visible portion (may be cut off)
    const displayUrl = fullText.slice(urlStart, Math.min(urlEnd, truncateAt));
    parts.push(linkAnchor(match[0], displayUrl, urlStart));

    lastIndex = Math.min(urlEnd, truncateAt);

    // URL ran past the truncation boundary — we've filled the window
    if (urlEnd >= truncateAt) break;
  }

  // Any remaining plain text before the truncation boundary
  if (lastIndex < truncateAt) {
    parts.push(fullText.slice(lastIndex, truncateAt));
  }

  return parts.length > 0 ? parts : [fullText.slice(0, truncateAt)];
}

export interface DataTableTextCellProps {
  /** Text content to display */
  text: string;
  /** Character limit before truncation (default: 80) */
  truncateAt?: number;
  /** Custom expand button text */
  expandText?: string;
  /** Custom collapse button text */
  collapseText?: string;
  /** Custom button className */
  buttonClassName?: string;
}

/**
 * Text cell component with expand/collapse functionality for long text
 * Used within DataTable for cells with lengthy content
 */
export function DataTableTextCell({
  text,
  truncateAt = 80,
  expandText = 'Read More',
  collapseText = 'Show Less',
  buttonClassName = 'ml-1 text-orange-600 hover:text-orange-500 text-sm',
}: DataTableTextCellProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const shouldTruncate = text.length > truncateAt;

  if (!shouldTruncate) {
    return <span>{linkifyText(text)}</span>;
  }

  return (
    <div>
      {isExpanded ? (
        <span>{linkifyText(text)}</span>
      ) : (
        <span>{linkifyTruncated(text, truncateAt)}...</span>
      )}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={buttonClassName}
        type="button"
      >
        {isExpanded ? collapseText : expandText}
      </button>
    </div>
  );
}
